import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Menu, MenuItem, ListItemIcon } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockResetIcon from '@mui/icons-material/LockReset';
import SecurityIcon from '@mui/icons-material/Security';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import { sa, PillButton } from '../superAdminUi';
import { ACCOUNT_STATUS, lifecycleAction } from './userLifecycle';

// A leading icon per action, matching the design mockup.
const ACTION_ICONS = {
  edit: EditOutlinedIcon,
  changeEmail: MailOutlineIcon,
  resetPassword: LockResetIcon,
  resetMfa: SecurityIcon,
  delete: DeleteOutlineIcon,
  reactivate: RestoreIcon,
};

// The per-user "Actions" dropdown. Delete is offered for active users, Reactivate for
// inactive/deleted ones — the destructive and revival actions are never shown together.
export const USER_ACTIONS = {
  edit: 'Edit User',
  changeEmail: 'Change Email',
  resetPassword: 'Reset Password',
  resetMfa: 'Reset MFA',
  delete: 'Delete User',
  reactivate: 'Reactivate',
};

function UserActionsMenu({ user, onSelect, showLifecycle = false }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const close = () => setAnchorEl(null);

  const choose = (key) => {
    close();
    onSelect(key, user);
  };

  // Off the default Active view, the lifecycle action is surfaced first with a status-aware
  // label (e.g. "Resend set-password link" for a pending user). Its handler key is unchanged,
  // so the existing action flow runs as-is; only the wording adapts.
  const life = showLifecycle ? lifecycleAction(user.status) : null;
  const labelFor = (key) => (life && life.key === key ? life.label : USER_ACTIONS[key]);

  // A deleted account is reactivated before it is edited.
  const baseKeys = user.status === ACCOUNT_STATUS.Deleted ? [] : ['edit'];
  baseKeys.push('changeEmail', 'resetPassword', 'resetMfa');
  baseKeys.push(user.isInactive ? 'reactivate' : 'delete');
  const keys = life ? [life.key, ...baseKeys.filter((k) => k !== life.key)] : baseKeys;

  return (
    <>
      <PillButton
        type='button'
        $variant='teal'
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
      >
        Actions
        <ArrowDropDownIcon fontSize='small' />
      </PillButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 208,
            borderRadius: sa.radius.md,
            border: `1px solid ${sa.color.stroke}`,
            boxShadow: '0 10px 28px rgba(17, 17, 17, 0.12)',
            py: 0.5,
          },
        }}
      >
        {keys.map((key) => {
          const Icon = ACTION_ICONS[key];
          const danger = key === 'delete';
          return (
            <MenuItem
              key={key}
              onClick={() => choose(key)}
              sx={{
                fontFamily: sa.font.family,
                fontSize: 13,
                minHeight: 'auto',
                py: 0.85,
                px: 1.5,
                color: danger ? sa.color.pink : sa.color.text,
                '&:hover': { background: sa.color.page },
              }}
            >
              {Icon && (
                <ListItemIcon sx={{ minWidth: 0, mr: 1.25 }}>
                  <Icon sx={{ fontSize: 18, color: danger ? sa.color.pink : sa.color.textGray }} />
                </ListItemIcon>
              )}
              {labelFor(key)}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

UserActionsMenu.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isInactive: PropTypes.bool,
    status: PropTypes.string,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
  showLifecycle: PropTypes.bool,
};

export default UserActionsMenu;
